import { z } from 'zod'
import { attachArticleExtrasSchema, attachSourceSchema } from './article-addition'
import { coverageRuleInputSchema, coverageRuleSchema } from './coverage'
import { issueSourceSchema, MAX_EMAIL_RECIPIENTS } from './report-issue'

/**
 * Real-time alerts (CLI-744) — the IA's sending assistant, ported from the analytics dashboard's
 * `/alerts` page and its 5-minute runner.
 *
 * The shape mirrors reports on purpose (Kaushik, 2026-09-25: "treat it the same way as reports"):
 * an RTA belongs to a SUB-ACCOUNT, a sub-account can hold several, names are unique within it,
 * and what it listens for is a set of TOPICS attached with the same `{topicId, dataSources}`
 * junction a report header uses. Nothing here picks individual queries inside a topic — an alert
 * that needs different terms uses its own topic, fresh or cloned (Slack thread, 2026-09-25).
 *
 * Delivery is Slack and email, at parity with the dashboard (Kaushik, 2026-09-29): every alert is
 * reviewed by an IA and sent per channel — one email per alert or a bundle, recipients from the
 * defaults or the first recipient group whose topics the alert matched. Email goes only through
 * `mailerFor`, so it answers 503 wherever EMAIL_SENDING isn't live, and reaches Clipbook
 * addresses only while INTERNAL_SEND_DOMAINS holds (issue-send.ts), like every Headline email.
 */

/**
 * The actor the runner writes into the audit trail. No users row: the events route names it
 * "Headline (automatic)". Same value as CLI-741's auto-send SYSTEM_ACTOR_ID, so one id means
 * "the system" everywhere.
 */
export const RTA_SYSTEM_ACTOR_ID = '00000000-0000-4000-8000-000000000000'

export const rtaStatusSchema = z.enum(['active', 'paused'])
export type RtaStatus = z.infer<typeof rtaStatusSchema>

/**
 * How the article's full text rides the Slack message. `off` = never; `link` = a "View full
 * content" link to the hosted page; `inline` = the text itself, trimmed to Slack's block limit.
 * Same three modes as legacy `alert_settings.full_text_mode`.
 */
export const rtaFullTextModeSchema = z.enum(['off', 'link', 'inline'])
export type RtaFullTextMode = z.infer<typeof rtaFullTextModeSchema>

/**
 * The families an RTA's data model can hold — the ones whose matches land in `hit_queries`
 * (broadcast does not: TVEyes clips arrive by webhook, outside the matcher). Stored junctions and
 * per-source Slack channels may name any of them, and keep parsing.
 */
export const RTA_SOURCES = ['news', 'x', 'linkedin', 'podcast', 'newsletter'] as const
export const rtaSourceSchema = z.enum(RTA_SOURCES)
export type RtaSource = (typeof RTA_SOURCES)[number]

/**
 * The families an RTA listens on: every one the model holds. Legacy alerted on whatever a listed
 * query matched, and a third of one client's alerts were LinkedIn, X and podcast (measured
 * 2026-09-29), so news-only was not parity. Social and podcast still arrive on the collectors'
 * report schedules rather than a fast lane — the same latency legacy had. Narrowing this list is
 * the whole switch; the runner ignores what is not here and the UI shows it as "Soon".
 *
 * @alias every source today; the separate name is the switch.
 */
export const RTA_LIVE_SOURCES: readonly RtaSource[] = RTA_SOURCES

export function isLiveRtaSource(source: string): source is RtaSource {
  return (RTA_LIVE_SOURCES as readonly string[]).includes(source)
}

const slackWebhookSchema = z
  .url()
  .max(500)
  .refine((url) => url.startsWith('https://hooks.slack.com/'), 'must be a Slack incoming webhook')

/** Legacy's default subject. Tokens: {{title}} {{source}} {{topic}} (or {{query}}) {{author}}. */
export const RTA_DEFAULT_SUBJECT = 'New alert: {{title}}'

export const rtaEmailSettingsSchema = z.object({
  to: z.array(z.email()).max(MAX_EMAIL_RECIPIENTS).default([]),
  bcc: z.array(z.email()).max(MAX_EMAIL_RECIPIENTS).default([]),
  replyTo: z.email().nullable().default(null),
  subjectTemplate: z.string().trim().min(1).max(300).default(RTA_DEFAULT_SUBJECT),
})
export type RtaEmailSettings = z.infer<typeof rtaEmailSettingsSchema>

export const rtaRecipientGroupSchema = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1).max(100),
  to: z.array(z.email()).max(MAX_EMAIL_RECIPIENTS).default([]),
  bcc: z.array(z.email()).max(MAX_EMAIL_RECIPIENTS).default([]),
  /** The topics whose alerts go to this group. */
  topicIds: z.array(z.uuid()).max(200).default([]),
})
export type RtaRecipientGroup = z.infer<typeof rtaRecipientGroupSchema>

export const rtaSettingsSchema = z.object({
  version: z.literal(1),
  slack: z
    .object({
      /** Where alerts post by default. Null = not configured; sending answers 409. */
      webhookUrl: slackWebhookSchema.nullable().default(null),
      /** Per source family, e.g. X posts to their own channel. Absent = `webhookUrl`. */
      webhookUrlsBySource: z
        .record(z.string(), slackWebhookSchema)
        .refine(
          (bySource) =>
            Object.keys(bySource).every((key) => rtaSourceSchema.safeParse(key).success),
          'keys must be RTA source families',
        )
        .default({}),
    })
    .default({ webhookUrl: null, webhookUrlsBySource: {} }),
  /** Terms highlighted in the snippet and full text, on top of what the matcher marked. */
  highlightTerms: z.array(z.string().trim().min(1).max(200)).max(100).default([]),
  fullTextMode: rtaFullTextModeSchema.default('off'),
  /** Email, legacy's settings one for one: recipients, reply-to, and the subject template. */
  email: rtaEmailSettingsSchema.default({
    to: [],
    bcc: [],
    replyTo: null,
    subjectTemplate: RTA_DEFAULT_SUBJECT,
  }),
  /**
   * Named recipient lists routed by topic: an alert goes to the FIRST group any of whose topics it
   * matched, else to the default To/BCC — legacy's rule, with topics where it had queries. An IA can
   * still pick a group when sending.
   */
  recipientGroups: z.array(rtaRecipientGroupSchema).max(50).default([]),
  /**
   * A migrated RTA the legacy analytics dashboard still delivers. Headline polls it and shows its
   * alerts, and refuses every send — email and Slack — until it is switched over, so a client never
   * hears the same alert twice: the reports' `delivery.legacyDelivery` rule. The migration writes
   * true; the Delivery tab's "Delivered by" switch is how an RTA moves.
   */
  legacyDelivery: z.boolean().default(false),
})
export type RtaSettings = z.infer<typeof rtaSettingsSchema>

/**
 * How many of the matcher's snippets an alert carries: one, as legacy does (it takes the latest).
 * Not a setting — the analytics dashboard never had one, and v0 ships what IAs already use.
 */
export const RTA_SNIPPETS_PER_ALERT = 1

export function defaultRtaSettings(): RtaSettings {
  return rtaSettingsSchema.parse({ version: 1 })
}

/**
 * A topic the RTA listens on. `dataSources` empty = every source the topic has enabled.
 * `coverage` is this topic's own rules ON THIS RTA — outlets, countries, languages — which
 * replace the RTA's per field, the way a header's rules replace its report's; empty inherits.
 * Legacy applied each header's outlet lists to that header's alert queries, and this is where
 * those lists live after migration.
 */
export const rtaTopicSchema = z.object({
  topicId: z.uuid(),
  dataSources: z.array(rtaSourceSchema).default([]),
  coverage: z.array(coverageRuleInputSchema).default([]),
})
export type RtaTopic = z.infer<typeof rtaTopicSchema>
/** What a caller sends: `dataSources` and `coverage` may be left out (every source; inherit). */
export type RtaTopicInput = z.input<typeof rtaTopicSchema>

export const rtaSummarySchema = z.object({
  id: z.uuid(),
  accountId: z.uuid(),
  subaccountId: z.uuid(),
  subaccountName: z.string(),
  name: z.string(),
  status: rtaStatusSchema,
  topicCount: z.number().int().nonnegative(),
  pendingCount: z.number().int().nonnegative(),
  /** The newest alert's arrival, for "last alert 3 min ago". */
  lastAlertAt: z.iso.datetime().nullable(),
})
export type RtaSummary = z.infer<typeof rtaSummarySchema>

export const rtaSchema = rtaSummarySchema.extend({
  settings: rtaSettingsSchema,
  topics: z.array(rtaTopicSchema.extend({ coverage: z.array(coverageRuleSchema) })),
  /** The RTA's own coverage rules; a topic with none of its own inherits them per field. */
  coverage: z.array(coverageRuleSchema),
  /** How far the runner has read `hit_queries.created_at`. Null until its first pass. */
  matchedThrough: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
})
export type Rta = z.infer<typeof rtaSchema>

export const listRtasQuerySchema = z.object({
  accountId: z.uuid().optional(),
  subaccountId: z.uuid().optional(),
})
export const listRtasResponseSchema = z.object({ rtas: z.array(rtaSummarySchema) })
export type ListRtasResponse = z.infer<typeof listRtasResponseSchema>

export const createRtaSchema = z.object({
  subaccountId: z.uuid(),
  name: z.string().trim().min(1).max(200),
})
export const updateRtaSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  status: rtaStatusSchema.optional(),
  settings: rtaSettingsSchema.optional(),
  /** Full replacement of the RTA's own rules, like a report's `coverage` patch. */
  coverage: z.array(coverageRuleInputSchema).optional(),
})
export const setRtaTopicsSchema = z.object({ topics: z.array(rtaTopicSchema).max(200) })

/**
 * Clone a topic for this RTA — the "separate RTA topic, fresh or cloned" decision (Slack thread,
 * 2026-09-25): a copy marked used for RTAs, named "<topic> – RTA" unless the IA names it, in the
 * RTA's own client and attached here. Only for when the content must differ — any topic can be
 * listened on as it is; a copy's queries can be tightened for alerting without moving a report.
 */
export const cloneRtaTopicSchema = z.object({
  topicId: z.uuid(),
  /** The RTA topic's name; defaults to "<topic> – RTA". */
  name: z.string().trim().min(1).max(200).optional(),
})

/** Pending counts for every RTA the caller can see — the session tabs' chips. */
export const rtaCountsResponseSchema = z.object({
  counts: z.record(z.string(), z.number().int().nonnegative()),
})
export type RtaCountsResponse = z.infer<typeof rtaCountsResponseSchema>

// ── Alerts ─────────────────────────────────────────────────────────────────────────────────────

/**
 * `dismissed` is the IA's "not relevant" — triaged and deliberately not sent. Distinct from
 * deleting (which is for mistakes and purges after 7 days): a dismissal stays, reopens, and counts
 * as a decision in the audit trail and in any later latency or precision roundup. Legacy's
 * `skipped`.
 */
export const rtaAlertStatusSchema = z.enum(['pending', 'sent', 'failed', 'dismissed'])
export type RtaAlertStatus = z.infer<typeof rtaAlertStatusSchema>

export const rtaAlertAddedViaSchema = z.enum(['match', 'manual'])

/** What the runner copied from the warehouse when the alert was created. Snippets are HTML with
 *  the matcher's terms in `<mark>`, the same shape a report item's snapshot carries. */
export const rtaAlertSnapshotSchema = z.object({
  headline: z.string().max(1000),
  url: z.string().max(2000),
  outlet: z.string().max(255).nullable().default(null),
  author: z.string().max(255).nullable().default(null),
  publishDate: z.iso.datetime().nullable().default(null),
  summary: z.string().max(4000).nullable().default(null),
  snippets: z.array(z.string().max(4000)).max(10).default([]),
  dataSource: z.string().max(40).nullable().default(null),
  reach: z.number().int().nullable().default(null),
})
export type RtaAlertSnapshot = z.infer<typeof rtaAlertSnapshotSchema>

/**
 * The IA's edits, sparse over the snapshot. `fullText` is HTML (bold and highlight survive), and is
 * the one place the app database holds licensed text: it exists only because an IA edited it, and
 * it is purged with the alert.
 */
export const rtaAlertOverridesSchema = z.object({
  headline: z.string().min(1).max(1000).optional(),
  /** The email subject, edited; absent = the RTA's subject template. */
  subject: z.string().min(1).max(300).optional(),
  outlet: z.string().min(1).max(255).optional(),
  author: z.string().min(1).max(255).optional(),
  url: z.url().max(2000).optional(),
  publishDate: z.iso.datetime().optional(),
  summary: z.string().max(4000).optional(),
  /** Plain words (the pre-CLI-769 editor); the matcher's marks are re-applied when rendered. */
  snippets: z.array(z.string().max(4000)).max(10).optional(),
  /**
   * Snippets as the rich editor saved them (CLI-769): HTML with bold, italic, underline, highlight
   * and links, rendered as written. Wins over `snippets`; a save of either clears the other.
   */
  snippetsHtml: z.array(z.string().max(8000)).max(10).optional(),
  fullText: z.string().max(200_000).optional(),
})
export type RtaAlertOverrides = z.infer<typeof rtaAlertOverridesSchema>

/** PATCH: `null` clears a key back to the snapshot, `undefined` leaves it. */
export const rtaAlertOverridesPatchSchema = z.object({
  headline: z.string().min(1).max(1000).nullable().optional(),
  subject: z.string().min(1).max(300).nullable().optional(),
  outlet: z.string().min(1).max(255).nullable().optional(),
  author: z.string().min(1).max(255).nullable().optional(),
  url: z.url().max(2000).nullable().optional(),
  publishDate: z.iso.datetime().nullable().optional(),
  summary: z.string().max(4000).nullable().optional(),
  snippets: z.array(z.string().max(4000)).max(10).nullable().optional(),
  snippetsHtml: z.array(z.string().max(8000)).max(10).nullable().optional(),
  fullText: z.string().max(200_000).nullable().optional(),
})
export type RtaAlertOverridesPatch = z.infer<typeof rtaAlertOverridesPatchSchema>

export const rtaAlertSchema = z.object({
  id: z.uuid(),
  rtaId: z.uuid(),
  hitId: z.uuid().nullable(),
  hitCreatedAt: z.iso.datetime().nullable(),
  source: issueSourceSchema,
  topicIds: z.array(z.uuid()),
  status: rtaAlertStatusSchema,
  addedVia: rtaAlertAddedViaSchema,
  snapshot: rtaAlertSnapshotSchema,
  overrides: rtaAlertOverridesSchema,
  /** When the matcher wrote the `hit_queries` row — the start of the latency clock. */
  matchedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  sentAt: z.iso.datetime().nullable(),
  sentBy: z.string().nullable(),
  /** Slack delivery was stubbed (`SLACK_SENDING` not live) — nothing reached a channel. */
  sentStubbed: z.boolean(),
  /** The channels this alert has gone out on — each channel is sent (and refused twice) apart. */
  sentChannels: z.array(z.enum(['email', 'slack'])),
  lastError: z.string().nullable(),
  deletedAt: z.iso.datetime().nullable(),
  deletedBy: z.string().nullable(),
})
export type RtaAlert = z.infer<typeof rtaAlertSchema>

/** How long a deleted alert can be restored, and how long the Deleted view keeps it. */
export const RTA_RESTORE_WINDOW_HOURS = 24
export const RTA_DELETED_RETENTION_DAYS = 7

export const rtaAlertSortSchema = z.enum(['newest', 'pending'])
export const rtaAlertViewSchema = z.enum(['active', 'deleted'])

export const listRtaAlertsQuerySchema = z.object({
  view: rtaAlertViewSchema.default('active'),
  status: rtaAlertStatusSchema.optional(),
  topicId: z.uuid().optional(),
  q: z.string().trim().max(200).optional(),
  sort: rtaAlertSortSchema.default('pending'),
  limit: z.coerce.number().int().min(1).max(500).default(200),
})
export type ListRtaAlertsQuery = z.infer<typeof listRtaAlertsQuerySchema>

export const listRtaAlertsResponseSchema = z.object({
  alerts: z.array(rtaAlertSchema),
  /** Counts across the whole RTA, not the filtered page — the status pills read these. */
  counts: z.object({
    pending: z.number().int().nonnegative(),
    sent: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    dismissed: z.number().int().nonnegative(),
    deleted: z.number().int().nonnegative(),
  }),
})
export type ListRtaAlertsResponse = z.infer<typeof listRtaAlertsResponseSchema>

export const rtaAlertDetailSchema = z.object({
  alert: rtaAlertSchema,
  /** The edited full text if there is one, else the warehouse's, else null. Never stored unless edited. */
  fullText: z.string().nullable(),
  fullTextEdited: z.boolean(),
})
export type RtaAlertDetail = z.infer<typeof rtaAlertDetailSchema>

export const rtaAlertIdsSchema = z.object({ alertIds: z.array(z.uuid()).min(1).max(500) })

export const rtaChannelSchema = z.enum(['email', 'slack'])
export type RtaChannel = z.infer<typeof rtaChannelSchema>

/** Send each alert on one channel. `recipientGroupId` overrides the topic routing for email. */
export const sendRtaAlertsSchema = rtaAlertIdsSchema.extend({
  channel: rtaChannelSchema.default('slack'),
  recipientGroupId: z.uuid().optional(),
})

/** Email several alerts as ONE message — legacy's "Send bundle". */
export const sendRtaBundleSchema = rtaAlertIdsSchema.extend({
  recipientGroupId: z.uuid().optional(),
  subject: z.string().trim().min(1).max(300).optional(),
})

/** What this environment can deliver — so the page can say why a send button is off. */
export const rtaDeliveryStatusSchema = z.object({
  /** `off` wherever EMAIL_SENDING isn't live: sends answer 503. */
  email: z.enum(['live', 'off']),
  /** `stub` wherever SLACK_SENDING isn't live: sends are logged, not posted. */
  slack: z.enum(['live', 'stub']),
  /** The delivery kill switch (CLI-741) holds every send, RTAs included. */
  paused: z.boolean(),
  /** Domains email may reach here (INTERNAL_SEND_DOMAINS); empty = any address (EMAIL_RECIPIENTS=any). */
  emailDomains: z.array(z.string()),
})
export type RtaDeliveryStatus = z.infer<typeof rtaDeliveryStatusSchema>

export const rtaBulkResultSchema = z.object({
  /** Alerts the action applied to. */
  done: z.array(z.uuid()),
  /** Alerts it could not apply to, and why — e.g. restore past the 24h window. */
  refused: z.array(z.object({ id: z.uuid(), reason: z.string() })),
})
export type RtaBulkResult = z.infer<typeof rtaBulkResultSchema>

export const addRtaAlertSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('hit'), hitId: z.uuid(), hitCreatedAt: z.iso.datetime() }),
  /**
   * Typed in by hand (CLI-625): attached to the warehouse under the RTA's topics first, so the
   * alert carries a real hit and every other product sees the article; the fields are the
   * attach's own. The source is read off the link when the person does not say.
   */
  attachArticleExtrasSchema.extend({
    kind: z.literal('manual'),
    source: attachSourceSchema.optional(),
    headline: z.string().trim().min(1).max(1000),
    url: z.url().max(2000),
  }),
])
export type AddRtaAlert = z.infer<typeof addRtaAlertSchema>

export const rtaHitSearchResultSchema = z.object({
  hitId: z.uuid(),
  hitCreatedAt: z.iso.datetime(),
  headline: z.string(),
  url: z.string(),
  outlet: z.string().nullable(),
  publishDate: z.iso.datetime().nullable(),
})
export const rtaHitSearchResponseSchema = z.object({
  results: z.array(rtaHitSearchResultSchema),
})

// ── Audit trail ────────────────────────────────────────────────────────────────────────────────

/**
 * Every change to an RTA or its alerts, append-only. Destructive actions carry enough of what was
 * there (`before`) to say what was lost; the purge that hard-deletes an alert after the retention
 * window writes one too, as the system actor, so a missing alert always has an explanation.
 */
export const rtaEventActionSchema = z.enum([
  'rta.create',
  'rta.update',
  'rta.topics',
  'rta.delete',
  'alert.match',
  'alert.add',
  'alert.edit',
  'alert.delete',
  'alert.restore',
  'alert.send',
  'alert.send_failed',
  'alert.dismiss',
  'alert.reopen',
  'alert.purge',
])
export type RtaEventAction = z.infer<typeof rtaEventActionSchema>

export const rtaEventSchema = z.object({
  id: z.uuid(),
  rtaId: z.uuid(),
  alertId: z.uuid().nullable(),
  action: rtaEventActionSchema,
  actorId: z.uuid(),
  /** Display name for the actor; "Headline (automatic)" for the runner. */
  actorName: z.string(),
  payload: z.record(z.string(), z.unknown()),
  createdAt: z.iso.datetime(),
})
export type RtaEvent = z.infer<typeof rtaEventSchema>

export const listRtaEventsResponseSchema = z.object({ events: z.array(rtaEventSchema) })

export const rtaRunResultSchema = z.object({
  rtas: z.number().int().nonnegative(),
  created: z.number().int().nonnegative(),
  purged: z.number().int().nonnegative(),
  skipped: z.array(z.object({ rtaId: z.uuid(), reason: z.string() })),
})
export type RtaRunResult = z.infer<typeof rtaRunResultSchema>
